import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Play, Pause, Loader2, Plus, Trash2, Music, Upload, Volume2, Clock
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const PreServicePanel = ({ onRefresh }) => {
  const [preServiceMode, setPreServiceMode] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [musicUrl, setMusicUrl] = useState('');
  const [musicSource, setMusicSource] = useState('none');
  const [uploadedMusic, setUploadedMusic] = useState([]);
  const [selectedMusicId, setSelectedMusicId] = useState('');
  const [rotationInterval, setRotationInterval] = useState(10);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { loadUploadedMusic(); }, []);

  const loadUploadedMusic = async () => {
    try {
      const res = await axios.get(`${API}/display/music`);
      setUploadedMusic(res.data || []);
    } catch (error) {
      console.error('Error loading music:', error);
    }
  };

  const handleMusicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
      const res = await axios.post(`${API}/display/upload-music`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        await loadUploadedMusic();
        setSelectedMusicId(res.data.music.id);
        setMusicUrl(res.data.music.file_url);
        setMusicSource('upload');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.detail || 'Failed to upload music file');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteMusic = async (musicId) => {
    if (!window.confirm('Delete this music file?')) return;
    try {
      await axios.delete(`${API}/display/music/${musicId}`);
      if (selectedMusicId === musicId) { setSelectedMusicId(''); setMusicUrl(''); }
      await loadUploadedMusic();
    } catch (error) {
      console.error('Error deleting music:', error);
    }
  };

  const handleSelectMusic = (music) => {
    setSelectedMusicId(music.id);
    setMusicUrl(music.file_url);
    setMusicSource('upload');
  };

  const getActiveMusicUrl = () => {
    if (musicSource === 'none') return '';
    if (musicSource === 'url') return musicUrl;
    if (musicSource === 'upload') {
      const selected = uploadedMusic.find(m => m.id === selectedMusicId);
      return selected ? selected.file_url : '';
    }
    return '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleStartPreService = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/display/show/preservice`, {
        announcements: announcements.map(a => a.text),
        music_url: getActiveMusicUrl() || null,
        rotation_interval: rotationInterval
      });
      setPreServiceMode(true);
      alert('Pre-service mode started!');
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to start pre-service mode');
    }
    setLoading(false);
  };

  const handleStopPreService = async () => {
    try {
      await axios.post(`${API}/display/show/welcome`);
      setPreServiceMode(false);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 data-testid="preservice-title" className="text-lg font-semibold text-slate-800">Pre-Service Mode</h3>
          <p className="text-sm text-slate-500">Display rotating announcements with background music before service</p>
        </div>
        {preServiceMode ? (
          <button data-testid="stop-preservice-btn" onClick={handleStopPreService}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700">
            <Pause className="w-4 h-4" /> Stop Pre-Service
          </button>
        ) : (
          <button data-testid="start-preservice-btn" onClick={handleStartPreService}
            disabled={loading || announcements.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Start Pre-Service
          </button>
        )}
      </div>

      {/* Announcements */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700">Rotating Announcements</label>
          <button data-testid="add-announcement-btn"
            onClick={() => setAnnouncements([...announcements, { id: Date.now(), text: '' }])}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {announcements.map((ann, idx) => (
            <div key={ann.id} className="flex gap-2">
              <input type="text" data-testid={`announcement-input-${idx}`}
                value={ann.text}
                onChange={(e) => setAnnouncements(announcements.map(a => a.id === ann.id ? { ...a, text: e.target.value } : a))}
                placeholder={`Announcement ${idx + 1}...`}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              <button onClick={() => setAnnouncements(announcements.filter(a => a.id !== ann.id))}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {announcements.length === 0 && (
            <p className="text-sm text-slate-400 py-4 text-center">No announcements yet. Add some to display during pre-service.</p>
          )}
        </div>
      </div>

      {/* Background Music */}
      <div className="space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <Music className="w-5 h-5 text-slate-600" />
          <h4 className="font-medium text-slate-800">Background Music</h4>
        </div>
        <div className="flex gap-2">
          {[{ id: 'none', label: 'No Music' }, { id: 'upload', label: 'Upload File' }, { id: 'url', label: 'URL Link' }].map(opt => (
            <button key={opt.id} data-testid={`music-source-${opt.id}`}
              onClick={() => setMusicSource(opt.id)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                musicSource === opt.id ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>

        {musicSource === 'upload' && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input ref={fileInputRef} type="file" accept=".mp3,.wav,.ogg,.m4a,.aac,.flac"
                onChange={handleMusicUpload} className="hidden" />
              <button data-testid="upload-music-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 text-sm disabled:opacity-50">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Uploading...' : 'Upload Music File'}
              </button>
              <span className="text-xs text-slate-400">MP3, WAV, OGG, M4A, AAC, FLAC (max 50MB)</span>
            </div>
            {uploadedMusic.length > 0 && (
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {uploadedMusic.map(music => (
                  <div key={music.id} data-testid={`music-item-${music.id}`}
                    className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                      selectedMusicId === music.id ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-slate-100 hover:bg-slate-50'
                    }`}
                    onClick={() => handleSelectMusic(music)}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedMusicId === music.id ? 'bg-blue-600' : 'bg-slate-200'}`}>
                      <Volume2 className={`w-4 h-4 ${selectedMusicId === music.id ? 'text-white' : 'text-slate-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{music.title}</p>
                      <p className="text-xs text-slate-400">{music.file_type?.toUpperCase()} · {formatFileSize(music.file_size)}</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteMusic(music.id); }}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {uploadedMusic.length === 0 && <p className="text-sm text-slate-400 text-center py-3">No music files uploaded yet</p>}
          </div>
        )}

        {musicSource === 'url' && (
          <div>
            <input type="url" data-testid="music-url-input" value={musicUrl}
              onChange={(e) => setMusicUrl(e.target.value)} placeholder="https://example.com/music.mp3"
              className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm" />
            <p className="text-xs text-slate-400 mt-1">Direct URL to an MP3 file</p>
          </div>
        )}
      </div>

      {/* Rotation Interval */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Rotation Interval (seconds)</label>
        <input type="number" data-testid="rotation-interval-input" value={rotationInterval}
          onChange={(e) => setRotationInterval(parseInt(e.target.value) || 10)} min="5" max="60"
          className="w-32 px-4 py-2 border border-slate-200 rounded-xl" />
      </div>

      {/* Preview */}
      <div className="p-4 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl text-white">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-amber-400">Preview</p>
          {musicSource !== 'none' && getActiveMusicUrl() && (
            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-white/10 rounded-full">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
              <span className="text-xs text-white/60">Music enabled</span>
            </div>
          )}
        </div>
        <div className="text-center py-8">
          {announcements.length > 0 ? (
            <p className="text-2xl">{announcements[0]?.text || 'Your announcement here...'}</p>
          ) : (
            <p className="text-white/50">Add announcements to see preview</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreServicePanel;
